import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Notification } from '@progress/kendo-react-notification';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import ArchiveIcon from '@mui/icons-material/Archive';
import CancelIcon from '@mui/icons-material/Cancel';
import { authService } from '../../..';
import { httpGetAsync, httpPostAsync, fetchConfig } from '../../../utils/HttpClient';
import localforage from 'localforage';

// Status enum matching backend ESignRequestStatus
enum ESignRequestStatus {
  Draft = 0,
  Sent = 1,
  Viewed = 2,
  PartiallySigned = 3,
  Completed = 4,
  Declined = 5,
  Expired = 6,
  Cancelled = 7,
  Failed = 8,
  InPersonPending = 9
}

const statusLabels: Record<number, string> = {
  [ESignRequestStatus.Draft]: 'Draft',
  [ESignRequestStatus.Sent]: 'Sent',
  [ESignRequestStatus.Viewed]: 'Viewed',
  [ESignRequestStatus.PartiallySigned]: 'Partially Signed',
  [ESignRequestStatus.Completed]: 'Completed',
  [ESignRequestStatus.Declined]: 'Declined',
  [ESignRequestStatus.Expired]: 'Expired',
  [ESignRequestStatus.Cancelled]: 'Cancelled',
  [ESignRequestStatus.Failed]: 'Failed',
  [ESignRequestStatus.InPersonPending]: 'In-Person Pending'
};

const statusColors: Record<number, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [ESignRequestStatus.Draft]: 'default',
  [ESignRequestStatus.Sent]: 'info',
  [ESignRequestStatus.Viewed]: 'info',
  [ESignRequestStatus.PartiallySigned]: 'warning',
  [ESignRequestStatus.Completed]: 'success',
  [ESignRequestStatus.Declined]: 'error',
  [ESignRequestStatus.Expired]: 'default',
  [ESignRequestStatus.Cancelled]: 'default',
  [ESignRequestStatus.Failed]: 'error',
  [ESignRequestStatus.InPersonPending]: 'warning'
};

interface ESignSigner {
  id: string;
  name: string;
  email: string;
  signerRole: number;
  status: number;
  viewedAt?: string;
  signedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  signingOrder: number;
  externalActionId?: string;
}

interface ESignRequest {
  id: string;
  applicationId: string;
  status: number;
  subject: string;
  message?: string;
  sentAt?: string;
  completedAt?: string;
  expiresAt?: string;
  providerUrl?: string;
  externalRequestId?: string;
  createdDate: string;
  signers: ESignSigner[];
  documents?: { loanDocumentId: string; fileName: string }[];
  eSignProvider?: { providerCode: string; name: string };
}

interface InPersonSigningLink {
  signerName: string;
  signerRole: string;
  signingUrl: string;
}

interface ESignHistoryDialogProps {
  applicationId: string;
  onClose: () => void;
}

export default function ESignHistoryDialog({ applicationId, onClose }: ESignHistoryDialogProps) {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<ESignRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [syncingRequestId, setSyncingRequestId] = useState<string | null>(null);
  
  // In-person signing links state
  const [showLinksDialog, setShowLinksDialog] = useState(false);
  const [linksRequestId, setLinksRequestId] = useState<string | null>(null);
  const [signingLinks, setSigningLinks] = useState<InPersonSigningLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState<string | null>(null);
  const [hostEmail, setHostEmail] = useState('');
  const [downloadingRequestId, setDownloadingRequestId] = useState<string | null>(null);
  
  // Cancel signing state
  const [cancelConfirmRequestId, setCancelConfirmRequestId] = useState<string | null>(null);
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);

  // Force archive state
  const [forceArchiveConfirm, setForceArchiveConfirm] = useState<Record<string, boolean>>({});
  const [archivingRequestId, setArchivingRequestId] = useState<string | null>(null);
  const [archiveSuccess, setArchiveSuccess] = useState<string | null>(null);

  const isSecureClose = (request: ESignRequest) => {
    return request.eSignProvider?.providerCode === 'SECURE_CLOSE';
  };

  const isZoho = (request: ESignRequest) => {
    return !request.eSignProvider || request.eSignProvider.providerCode === 'ZOHO';
  };

  const getApiPrefix = (request: ESignRequest) => {
    return isSecureClose(request) ? 'api/secureClose' : 'api/zohoSign';
  };

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const bearerToken = await authService.getToken();
      
      // Fetch from both providers in parallel
      const [zohoResponse, scResponse] = await Promise.all([
        httpGetAsync(`api/zohoSign/requests/${applicationId}`, bearerToken!).catch(() => null),
        httpGetAsync(`api/secureClose/requests/${applicationId}`, bearerToken!).catch(() => null),
      ]);
      
      let allRequests: ESignRequest[] = [];
      
      if (zohoResponse?.ok) {
        const zohoData = await zohoResponse.json();
        allRequests = allRequests.concat(zohoData || []);
      }
      
      if (scResponse?.ok) {
        const scData = await scResponse.json();
        // Deduplicate by id in case both endpoints return same requests
        const existingIds = new Set(allRequests.map(r => r.id));
        const newRequests = (scData || []).filter((r: ESignRequest) => !existingIds.has(r.id));
        allRequests = allRequests.concat(newRequests);
      }
      
      // Sort by created date descending
      allRequests.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
      setRequests(allRequests);
      
      if (!zohoResponse?.ok && !scResponse?.ok) {
        setError('Failed to load e-sign history');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load e-sign history');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const canCancel = (request: ESignRequest) => {
    return isSecureClose(request) && [
      ESignRequestStatus.Sent,
      ESignRequestStatus.Viewed,
      ESignRequestStatus.PartiallySigned,
    ].includes(request.status);
  };

  const handleCancelSigning = async (requestId: string) => {
    setCancellingRequestId(requestId);
    setCancelConfirmRequestId(null);

    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        `api/secureClose/request/${requestId}/cancel`,
        {},
        bearerToken!
      );

      if (response?.ok) {
        await loadHistory();
      } else {
        const errorText = await response?.text();
        setError(errorText || 'Failed to cancel signing');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel signing');
    } finally {
      setCancellingRequestId(null);
    }
  };

  const handleSyncStatus = async (requestId: string) => {
    setSyncingRequestId(requestId);
    
    try {
      const request = requests.find(r => r.id === requestId);
      const apiPrefix = request ? getApiPrefix(request) : 'api/zohoSign';
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        `${apiPrefix}/request/${requestId}/sync`,
        {},
        bearerToken!
      );
      
      if (response?.ok) {
        // Refresh the full list to get updated status
        await loadHistory();
      } else {
        const errorText = await response?.text();
        setError(errorText || 'Failed to sync status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync status');
    } finally {
      setSyncingRequestId(null);
    }
  };

  const handleDownload = async (requestId: string) => {
    setDownloadingRequestId(requestId);
    
    try {
      const bearerToken = await authService.getToken();
      const config: any = await fetchConfig();
      const clientId = await localforage.getItem('clientId') as string | undefined;
      
      let url = `${config.api_endpoint}/api/zohoSign/request/${requestId}/download`;
      if (clientId) {
        url = `${url}?clientId=${clientId}`;
      }
      
      const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${bearerToken}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const contentType = response.headers.get('Content-Type') || '';
        
        // Default to .zip since Zoho returns a ZIP package
        let fileName = contentType.includes('zip') ? 'signed_documents.zip' : 'signed_documents.pdf';
        
        if (contentDisposition) {
          // Match both filename= and filename*= (RFC 5987)
          const fileNameStarMatch = contentDisposition.match(/filename\*="?([^";\s]+)"?/);
          const fileNameMatch = contentDisposition.match(/filename="?([^";\s]+)"?/);
          
          if (fileNameStarMatch) {
            fileName = decodeURIComponent(fileNameStarMatch[1]);
          } else if (fileNameMatch) {
            fileName = fileNameMatch[1];
          }
        }
        
        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorText = await response.text();
        setError(errorText || 'Failed to download documents');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download documents');
    } finally {
      setDownloadingRequestId(null);
    }
  };

  const handleForceArchive = async (requestId: string) => {
    setArchivingRequestId(requestId);
    setArchiveSuccess(null);
    setError(null);
    
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        `api/zohoSign/request/${requestId}/forceArchive`,
        {},
        bearerToken!
      );
      
      if (response?.ok) {
        const data = await response.json();
        setArchiveSuccess(`Successfully archived ${data.documentsArchived} document(s) to the application.`);
        // Clear the confirmation checkbox
        setForceArchiveConfirm(prev => ({ ...prev, [requestId]: false }));
      } else {
        const errorText = await response?.text();
        setError(errorText || 'Failed to archive documents');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to archive documents');
    } finally {
      setArchivingRequestId(null);
    }
  };

  const handleGetLinks = async (requestId: string) => {
    const request = requests.find(r => r.id === requestId);
    setLinksRequestId(requestId);
    setShowLinksDialog(true);
    setLinksLoading(true);
    setLinksError(null);
    setSigningLinks([]);
    
    try {
      const bearerToken = await authService.getToken();

      if (request && isSecureClose(request)) {
        // SecureClose: call get-links endpoint
        const response = await httpPostAsync(
          `api/secureClose/request/${requestId}/get-links`,
          {},
          bearerToken!
        );
        if (response?.ok) {
          const result = await response.json();
          if (result.success && result.signerLinks?.length > 0) {
            setSigningLinks(result.signerLinks);
          } else {
            setLinksError('No signing links available');
          }
        } else {
          const errorText = await response?.text();
          setLinksError(errorText || 'Failed to get signing links');
        }
      } else {
        // Zoho: existing behavior
        const response = await httpPostAsync(
          `api/zohoSign/regenerate-links/${requestId}`,
          { hostEmail: hostEmail || undefined },
          bearerToken!
        );
        if (response?.ok) {
          const result = await response.json();
          if (result.success && result.inPersonSigningLinks?.length > 0) {
            setSigningLinks(result.inPersonSigningLinks);
          } else {
            setLinksError(result.errorMessage || 'No signing links available');
          }
        } else {
          const errorText = await response?.text();
          setLinksError(errorText || 'Failed to get signing links');
        }
      }
    } catch (err: any) {
      setLinksError(err.message || 'Failed to get signing links');
    } finally {
      setLinksLoading(false);
    }
  };

  const handleRegenerateLinks = async () => {
    if (!linksRequestId) return;
    
    setLinksLoading(true);
    setLinksError(null);
    
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        `api/zohoSign/regenerate-links/${linksRequestId}`,
        { hostEmail: hostEmail || undefined },
        bearerToken!
      );
      
      if (response?.ok) {
        const result = await response.json();
        if (result.success && result.inPersonSigningLinks?.length > 0) {
          setSigningLinks(result.inPersonSigningLinks);
          setLinksError(null);
        } else {
          setLinksError(result.errorMessage || 'Failed to regenerate links');
        }
      } else {
        const errorText = await response?.text();
        setLinksError(errorText || 'Failed to regenerate links');
      }
    } catch (err: any) {
      setLinksError(err.message || 'Failed to regenerate links');
    } finally {
      setLinksLoading(false);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getSignerRoleLabel = (role: number) => {
    const roles: Record<number, string> = {
      1: 'Buyer',
      2: 'Co-Buyer',
      3: 'Dealer',
      4: 'Employee',
      5: 'Lender',
      6: 'Co-Buyer 2',
      7: 'Third Party Owner'
    };
    return roles[role] || 'Unknown';
  };

  const getSignerStatusSummary = (signers: ESignSigner[]) => {
    const signed = signers.filter(s => s.signedAt).length;
    return `${signed}/${signers.length} signed`;
  };

  const isInPersonRequest = (request: ESignRequest) => {
    return request.status === ESignRequestStatus.InPersonPending;
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="primary" />
            E-Sign History
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Notification type={{ style: 'error', icon: true }} style={{ marginBottom: 16 }}>
              {error}
              <Button onClick={loadHistory} size="small" sx={{ ml: 2 }}>
                Retry
              </Button>
            </Notification>
          ) : requests.length === 0 ? (
            <Notification type={{ style: 'info', icon: true }}>
              No e-sign requests found for this application.
            </Notification>
          ) : (
            <Box>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadHistory}
                  size="small"
                >
                  Refresh List
                </Button>
              </Box>
              
              {requests.map((request) => (
                <Paper key={request.id} sx={{ mb: 2, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() => setExpandedRequestId(
                      expandedRequestId === request.id ? null : request.id
                    )}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <IconButton size="small">
                        {expandedRequestId === request.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {request.subject || 'Untitled Request'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {request.eSignProvider?.name && (
                            <Chip 
                              label={request.eSignProvider.name} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mr: 1, fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          Created: {formatDate(request.createdDate)}
                          {request.sentAt && ` • Sent: ${formatDate(request.sentAt)}`}
                          {isSecureClose(request) && request.externalRequestId && (
                            <> • ID: <span
                              style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                              title="Click to copy"
                              onClick={(e) => { e.stopPropagation(); handleCopyLink(request.externalRequestId!); }}
                            >{request.externalRequestId}</span></>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        {getSignerStatusSummary(request.signers)}
                      </Typography>
                      <Chip
                        label={statusLabels[request.status] || 'Unknown'}
                        color={statusColors[request.status] || 'default'}
                        size="small"
                      />
                      <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={`Sync status from ${isSecureClose(request) ? 'SecureClose' : 'ZohoSign'}`}>
                          <IconButton
                            size="small"
                            onClick={() => handleSyncStatus(request.id)}
                            disabled={syncingRequestId === request.id}
                          >
                            {syncingRequestId === request.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <SyncIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>

                        {canCancel(request) && (
                          <Tooltip title="Cancel Signing">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setCancelConfirmRequestId(request.id)}
                              disabled={cancellingRequestId === request.id}
                            >
                              {cancellingRequestId === request.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <CancelIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {request.providerUrl && isZoho(request) && (
                          <Tooltip title="View in ZohoSign">
                            <IconButton
                              size="small"
                              href={request.providerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isInPersonRequest(request) && isZoho(request) && (
                          <Tooltip title="Get In-Person Signing Links">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleGetLinks(request.id)}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isZoho(request) && (
                        <Tooltip title="Download Documents">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(request.id)}
                            disabled={downloadingRequestId === request.id}
                          >
                            {downloadingRequestId === request.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  
                  <Collapse in={expandedRequestId === request.id}>
                    <Box sx={{ px: 2, pb: 2, pt: 1, backgroundColor: 'grey.50' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Signers
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Order</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {request.signers
                            .sort((a, b) => a.signingOrder - b.signingOrder)
                            .map((signer) => (
                              <TableRow key={signer.id}>
                                <TableCell>{signer.signingOrder}</TableCell>
                                <TableCell>{signer.name}</TableCell>
                                <TableCell>{getSignerRoleLabel(signer.signerRole)}</TableCell>
                                <TableCell>{signer.email}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={statusLabels[signer.status] || 'Unknown'}
                                    color={statusColors[signer.status] || 'default'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                      
                      {request.documents && request.documents.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Documents ({request.documents.length})
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.documents.map(d => d.fileName).join(', ')}
                          </Typography>
                          
                          {/* Force Archive Section - only show for completed Zoho requests */}
                          {request.status === ESignRequestStatus.Completed && isZoho(request) && (
                          <Box mt={2} p={2} sx={{ bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Manual Archive:</strong> Use this if signed documents were not automatically archived after completion.
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={forceArchiveConfirm[request.id] || false}
                                    onChange={(e) => setForceArchiveConfirm(prev => ({ 
                                      ...prev, 
                                      [request.id]: e.target.checked 
                                    }))}
                                    disabled={archivingRequestId === request.id}
                                  />
                                }
                                label={<Typography variant="body2">Confirm download and archive signed documents</Typography>}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={archivingRequestId === request.id ? <CircularProgress size={16} /> : <ArchiveIcon />}
                                onClick={() => handleForceArchive(request.id)}
                                disabled={!forceArchiveConfirm[request.id] || archivingRequestId === request.id}
                              >
                                {archivingRequestId === request.id ? 'Archiving...' : 'Archive to Documents'}
                              </Button>
                            </Box>
                            {archiveSuccess && archivingRequestId === null && (
                              <Notification type={{ style: 'success', icon: true }} style={{ marginTop: 8 }}>
                                {archiveSuccess}
                              </Notification>
                            )}
                          </Box>
                          )}
                        </Box>
                      )}
                      
                      {request.completedAt && (
                        <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                          ✓ Completed: {formatDate(request.completedAt)}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* In-Person Signing Links Dialog */}
      <Dialog
        open={showLinksDialog}
        onClose={() => setShowLinksDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon color="primary" />
            Signing Links
          </Box>
        </DialogTitle>
        <DialogContent>
          {linksLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : linksError ? (
            <Notification type={{ style: 'error', icon: true }} style={{ marginBottom: 16 }}>
              {linksError}
            </Notification>
          ) : signingLinks.length > 0 ? (
            <>
              <Notification type={{ style: 'success', icon: true }} style={{ marginBottom: 16 }}>
                Signing links retrieved successfully.
              </Notification>
            </>
          ) : (
            <Notification type={{ style: 'info', icon: true }}>
              Click "Get Links" to retrieve signing URLs for each signer.
            </Notification>
          )}

          {(() => {
            const linksRequest = requests.find(r => r.id === linksRequestId);
            return linksRequest && isZoho(linksRequest) ? (
              <TextField
                fullWidth
                label="Host Email (for sending links)"
                value={hostEmail}
                onChange={(e) => setHostEmail(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
                helperText="Click 'Regenerate & Email' to get new links and email them to this address"
              />
            ) : null;
          })()}
          
          {signingLinks.map((link, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {link.signerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.signerRole}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleCopyLink(link.signingUrl)}
                    startIcon={<ContentCopyIcon />}
                  >
                    Copy Link
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    href={link.signingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon />}
                  >
                    Open
                  </Button>
                </Box>
              </Box>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          {(() => {
            const linksRequest = requests.find(r => r.id === linksRequestId);
            if (linksRequest && isSecureClose(linksRequest)) {
              return (
                <Button
                  onClick={() => handleGetLinks(linksRequestId!)}
                  disabled={linksLoading}
                  startIcon={linksLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {linksLoading ? 'Loading...' : 'Refresh Links'}
                </Button>
              );
            }
            return (
              <Button
                onClick={handleRegenerateLinks}
                disabled={linksLoading}
                startIcon={linksLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {linksLoading 
                  ? 'Loading...' 
                  : hostEmail 
                    ? 'Regenerate & Email Links' 
                    : 'Regenerate Links'}
              </Button>
            );
          })()}
          <Button onClick={() => setShowLinksDialog(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel SecureClose Signing Confirmation */}
      <Dialog
        open={!!cancelConfirmRequestId}
        onClose={() => setCancelConfirmRequestId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Signing?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this signing request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmRequestId(null)}>No, Keep It</Button>
          <Button
            onClick={() => handleCancelSigning(cancelConfirmRequestId!)}
            color="error"
            variant="contained"
          >
            Yes, Cancel Signing
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
